import React, { useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import clsx from 'clsx';
import _get from 'lodash/get';
import _has from 'lodash/has';

import { useFeatures } from '@atc/features';
import { formatCurrency } from '@atc/string-fns';

import {
    Badge,
    Button,
    Col,
    ExpandTrigger,
    Image,
    List,
    Panel,
    Row,
    Text,
} from 'reaxl';
import { sendImpressions } from 'reaxl-analytics';
import { EmailCTA } from 'reaxl-email';
import {
    OwnerAddress,
    OwnerPhoneNumber,
    OwnerWebsiteCTA,
} from 'reaxl-molecules';

import { getAlphaListingType } from '@/utilities/getAlphaTaggingType';

import { inventoryImpressions } from '@/analytics/srpAnalyticsHandlers';

import srpActiveInteractionDuck from '@/ducks/srp/srpActiveInteractionDuck.js';

import AlphaModule from '@/modules/AlphaModule';

/**
 *  This component displays dealer specific info in a positioned overlay grid which can be used on
 *  special types of listings like the AlphaShowcase
 *
 *  Layout is the same on landscape and portrait:
 *
 * ,-------------------------------------
 * | OwnerName         |                 |
 * |                   |    OwnerLogo    |
 * |                   |    Tagline      |
 * |                   |.................|
 * | Address           |                 |
 * | PhoneNumber | CTA |  AccelerateTile |
 *  -------------------------------------
 *
 */

function InventoryDealerInfo({
    clickType,
    emailOwnerProps = {},
    listing = {},
    listings = [],
    listingsCount,
    onCollapsibleHeaderClick = () => { },
    onInventoryListingClick = () => { },
    onTileClick = () => { },
    onViewOwnerInventoryClick = () => { },
    orientation,
    owner = {},
    parentId,
    uiContext,
}) {
    const {
        show_alpha_criteria: [enableUnmatchedCriteria],
    } = useFeatures([
        'show_alpha_criteria',
    ]);

    const {
        id: ownerId,
        location,
        logo,
        name,
        phone,
        website = {},
        hideEmailForm,
    } = owner;

    const dispatch = useDispatch();
    const hasOwnerWebsite = !!website.href;
    const isPortrait = orientation === 'portrait';
    const titleTextClasses = clsx('text-gray-base', {
        'text-size-200': uiContext === 'inventory',
        'text-size-500 text-bold': uiContext === 'owner',
    });

    const showAlphaCarousel = _get(useSelector(srpActiveInteractionDuck.selectors.getDuckState), 'showAlphaCarousel', false);
    const isAlphaMarket = useSelector(AlphaModule.duck.selectors.isAlphaMarket);

    const showViewOwnerInventory = onViewOwnerInventoryClick instanceof Function;

    useEffect(() => {
        if (uiContext === 'owner') {
            const selectedItemIds = listings.slice(0, 2).map(({ id }) => (id));
            sendImpressions({
                name: inventoryImpressions,
                data: {
                    cmp: 'dealer_alpha',
                    selectedItemIds,
                    pixallData: {
                        label: 'dealer-vehicle-alpha',
                        eventSource: 'vehicle',
                        listingType: getAlphaListingType(isAlphaMarket, true),
                        impressionType: 'srpi',
                    },
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ownerId, uiContext, isAlphaMarket]);

    const analyticsData = {
        inventoryId: listing.id,
        ownerId,
        par: parentId,
        clickType: 'alpha',
    };

    const renderDealerLogo = logo && (
        <Image
            className="margin-bottom-1 cursor-pointer"
            alt="Dealer Logo"
            src={logo?.src}
            lazyload={false}
            loading="eager"
            onClick={(event) => onTileClick(event, owner)}
            width={71}
            height={30}
        />
    );

    const renderOffsiteOwnerTitle = (
        <OwnerWebsiteCTA
            analyticsData={analyticsData}
            className={titleTextClasses}
            href={website.href}
            key="offsiteOwnerTitle"
            label={name || listing.ownerName}
            uiContext="link"
        />
    );

    const renderOwnerTitle = (
        <span
            className={titleTextClasses}
            key="ownerTitle"
        >
            {name}
        </span>
    );

    const renderTitle = (
        <div className="margin-bottom-3">
            {hasOwnerWebsite ? renderOffsiteOwnerTitle : renderOwnerTitle}
        </div>
    );

    const address = location && location.address;
    const renderAddress = address && (
        <OwnerAddress
            uiContext="default"
            location={location}
            analyticsData={analyticsData}
            wrapperComponent="div"
            linkClassName="text-gray-base text-size-200"
        />
    );

    const renderOwnerPhone = (
        <OwnerPhoneNumber
            key="ownerPhoneNumber"
            className={clsx({ 'text-link link-undecorated': isPortrait })}
            phone={listing.phone || phone}
            isVisible
            isCallable={isPortrait}
        />
    );

    const renderEmailOwner = (
        <EmailCTA
            clickType={clickType}
            parentId={emailOwnerProps.parentId}
            key="email-cta"
            listingId={listing.id}
            onClick={emailOwnerProps.onCTAClick}
            ownerId={ownerId}
            type={isPortrait ? 'block' : 'link'}
            buttonSize="small"
            buttonStyle="default"
            showIcon={false}
            labelKey={emailOwnerProps.labelKey}
        />
    );

    const showOwnerPhoneNumber = _has(owner, 'phone.value');
    const showOwnerEmailCTA = !!emailOwnerProps && !hideEmailForm;

    const renderDealerInfoList = () => {
        const items = [];
        if (showOwnerPhoneNumber) {
            items.push(renderOwnerPhone);
        }
        if (!isPortrait && showOwnerEmailCTA) {
            items.push(renderEmailOwner);
        }

        return (
            <List
                inline
                items={items}
                className="text-gray-base text-size-200"
            />
        );
    };

    const assignImages = (ownerListing) => {
        let imageArray = [];

        // iterate through premimum spotlight images, up to 4
        const filteredImages = ownerListing?.images?.sources?.filter((image) => image.src !== undefined);
        if (filteredImages?.length) {
            imageArray = filteredImages.slice(0, 1);
        }

        return imageArray;
    };

    const renderListings = listings.slice(0, 2).map((ownerListing, index) => {
        const vehiclePricingDetail = ownerListing?.pricingDetail;
        const vehiclePrice = vehiclePricingDetail?.incentive || vehiclePricingDetail?.salePrice || vehiclePricingDetail?.msrp;
        const onInventoryTitleClick = (event) => onInventoryListingClick(event, ownerListing, index, clickType);

        return (
            <div
                className="col-xs-6 col-sm-5"
                key={`owner-listing-${ownerListing.id}-${index}`}
            >
                <Panel className="margin-0">
                    <div className="display-flex">
                        {assignImages(ownerListing)?.map((image) => (
                            <Image
                                key={`listing-alert-image-${ownerListing.id}-${index}`}
                                className="margin-top-1 margin-top-sm-0"
                                src={image?.src}
                                alt={image?.alt}
                                height={60}
                                width={80}
                            />
                        ))}
                        <div
                            className="padding-2 text-size-200 text-overflow"
                        >
                            <Button
                                bsStyle="text"
                                onClick={onInventoryTitleClick}
                            >
                                {ownerListing?.title}
                            </Button>
                            <Text
                                componentClass="div"
                                color="gray-base"
                                size={300}
                            >
                                {formatCurrency(vehiclePrice)}
                            </Text>
                        </div>
                    </div>
                </Panel>
            </div>
        );
    });

    const renderDealerInventoryButton = (
        <Button
            data-cmp="ownerViewCarsCTA"
            onClick={(event) => onViewOwnerInventoryClick(event, owner)}
            bsStyle="text"
        >
            View All Cars
        </Button>
    );

    const handleExpandTriggerClick = (event) => {
        event.stopPropagation();
        event.preventDefault();

        onCollapsibleHeaderClick();

        dispatch(srpActiveInteractionDuck.creators.setKeys({
            showAlphaCarousel: !showAlphaCarousel,
        }));
    };

    if (uiContext === 'owner') {
        return (
            <>
                {renderTitle}
                <hr />
                <Row className="display-flex justify-content-between ">
                    <Col
                        sm={9}
                        xs={12}
                        key="contactDealer"
                    >
                        {renderAddress}
                        {renderDealerInfoList()}
                    </Col>
                    <Col
                        className="hidden-xs padding-top-3 padding-top-sm-0 text-sm-right"
                        sm={3}
                    >
                        {renderDealerLogo}
                    </Col>
                </Row>
                <Row className="margin-top-auto display-sm-flex align-items-center">
                    {renderListings}
                    <Col
                        className="padding-0 text-center margin-top-3 margin-top-sm-0"
                        sm={2}
                        xs={12}
                    >
                        {showViewOwnerInventory && renderDealerInventoryButton}
                    </Col>
                </Row>
            </>
        );
    }

    const dealerLogoAndTrigger = (
        <>
            <div>{renderDealerLogo}</div>
            <div className="display-flex align-items-center pull-right margin-top-sm-2">
                <Badge className="padding-vertical-1 margin-right-2">
                    {listingsCount}
                </Badge>
                <ExpandTrigger
                    collapsed={{ label: 'View Vehicles', glyph: 'chevron-down' }}
                    data-cmp="alpha-expand-trigger"
                    expanded={{ label: 'Hide Vehicles', glyph: 'chevron-up' }}
                    isCollapsed={!showAlphaCarousel}
                    onClick={handleExpandTriggerClick}
                    className="text-size-200 padding-vertical-1"
                />
            </div>
        </>
    );

    return (
        <Row className="margin-bottom-1">
            {!isPortrait && (
                <Col
                    xs={12}
                    sm={7}
                    md={7}
                    lg={8}
                >
                    {enableUnmatchedCriteria && (
                        <Text
                            color="gray-base"
                            size={200}
                        >
                            Sponsored by
                            {' '}
                        </Text>
                    )}
                    {hasOwnerWebsite ? renderOffsiteOwnerTitle : renderOwnerTitle}
                    <div
                        className="display-flex flex-row justify-content-start flex-wrap margin-bottom-2"
                        key="contactDealer"
                    >
                        {renderDealerInfoList()}
                    </div>
                </Col>
            )}
            <Col
                className="align-items-center justify-content-between padding-top-3 padding-top-sm-0 text-sm-right"
                lg={4}
                sm={5}
                xs={12}
            >
                {isPortrait ? (
                    <div className="display-flex align-items-center justify-content-between">
                        {dealerLogoAndTrigger}
                    </div>
                ) : (
                    dealerLogoAndTrigger
                )}
            </Col>
        </Row>
    );
}

InventoryDealerInfo.propTypes = {
    /**
     * Click type for analytics
     */
    clickType: PropTypes.string,
    /**
     * The emailOwnerProps Object has the props to pass through to the ConnectedEmailOwner component.
     */
    emailOwnerProps: PropTypes.object,
    /**
     * The Listing data to display
     */
    listing: PropTypes.object,
    /**
     * Array of listings to display
     */
    listings: PropTypes.array,
    /**
     * Count of listings available
     */
    listingsCount: PropTypes.number,
    /**
     * Handler for collapsible header click
     */
    onCollapsibleHeaderClick: PropTypes.func,
    /**
     * The handler for inventory title click
     */
    onInventoryListingClick: PropTypes.func,
    /**
     * The handler for the tile click
     */
    onTileClick: PropTypes.func,
    /**
     * Handler for view owner inventory click
     */
    onViewOwnerInventoryClick: PropTypes.func,
    /**
     * Device orientation
     */
    orientation: PropTypes.oneOf(['portrait', 'landscape']),
    /**
     * The owner data to display in case a listing is not using this component
     */
    owner: PropTypes.object,
    /**
     * analytics parameter
     */
    parentId: PropTypes.string,
    /**
     * uiContext to control display of inventory listing side bar
     */
    uiContext: PropTypes.oneOf(['owner', 'inventory']),
};

export default InventoryDealerInfo;
